import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuthRedirectPath } from './authRedirect';
import { trackGuestUpgradeCredentialsReady } from './analytics';
import { clearGuestEmailSetup, hasGuestEmailSetup } from './guestUpgrade';
import { supabase } from './supabase';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiSave,
  FiArrowLeft,
  FiInfo
} from 'react-icons/fi';
import LanguageSelector from './LanguageSelector';
import { useI18n } from './i18n';
import './auth-pages.css';

function ResetPasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isCheckingLink, setIsCheckingLink] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const authRedirectPath = getAuthRedirectPath(location.state);
  const { t } = useI18n();

  useEffect(() => {
    let isActive = true;

    const resolveSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          if (isActive) {
            setHasSession(true);
            setIsCheckingLink(false);
          }
          return;
        }

        const url = new URL(window.location.href);
        const code = url.searchParams.get('code');
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (!error && data.session) {
            if (isActive) {
              setHasSession(true);
              setIsCheckingLink(false);
            }
            return;
          }
        }

        if (isActive) {
          setHasSession(false);
          setIsCheckingLink(false);
        }
      } catch (error) {
        if (isActive) {
          setHasSession(false);
          setIsCheckingLink(false);
        }
      }
    };

    resolveSession();

    return () => {
      isActive = false;
    };
  }, []);

  const changePassword = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setError('');
    setSuccess('');

    if (newPassword.length < 8) {
      setError(t('auth.passwordLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) throw error;

      const { data } = await supabase.auth.getSession();
      if (hasGuestEmailSetup(data?.session?.user?.id)) {
        trackGuestUpgradeCredentialsReady(data.session.user);
        clearGuestEmailSetup();
      }

      setSuccess(t('auth.passwordUpdatedRedirect'));
      setTimeout(() => {
        navigate(authRedirectPath);
      }, 1500);
    } catch (error) {
      setError(t('auth.passwordUpdateFailed', { message: error.message || t('auth.unexpected') }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <LanguageSelector />
        <div className="auth-header">
          <h1>{t('auth.resetTitle')}</h1>
          <p>{t('auth.chooseNewPassword')}</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>{success}</span>
          </div>
        )}

        {isCheckingLink ? (
          <div className="auth-warning">
            <FiInfo />
            <span>{t('auth.checkingResetLink')}</span>
          </div>
        ) : !hasSession ? (
          <>
            <div className="auth-warning">
              <FiAlertCircle />
              <span>{t('auth.invalidResetLink')}</span>
            </div>
            <button
              type="button"
              className="auth-btn secondary-btn"
              onClick={() => navigate('/login')}
            >
              <FiArrowLeft /> {t('auth.backToSignIn')}
            </button>
          </>
        ) : (
          <form className="auth-form" onSubmit={changePassword}>
            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder={t('auth.newPassword')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-btn link-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder={t('auth.confirmNewPassword')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="auth-btn link-btn"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>

            <button type="submit" className="auth-btn primary-btn" disabled={isSubmitting}>
              <FiSave /> {isSubmitting ? t('common.updating') : t('auth.updatePassword')}
            </button>

            <button
              type="button"
              className="auth-btn secondary-btn"
              onClick={() => navigate('/login')}
              disabled={isSubmitting}
            >
              <FiArrowLeft /> {t('auth.backToSignIn')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordPage;
